/**
 * CoverageNote — "Based on 24 of 26 alerts ⓘ" under an average card on Alert Details
 * and Hourly Summary (user, 2026-10-10).
 *
 * Total Events counts every listed alert, System Outages included, so it matches the
 * detail toggle. An average can only use alerts with a measurable time, and this line
 * says how many that was, so a reader can see where the number comes from. The tip
 * names what was left out.
 *
 * `coverage` is the Lambda's coverageSummary() (serverless/mysql_report.py), counted
 * from the same detail rows and the same null test as the average, so the two cannot
 * disagree: { total, response_measured, service_measured, outages }. Outages are the
 * one exclusion a row names itself. The rest of total - measured is what the event
 * report procs return NULL for:
 *   response  only an auto-clear (reason -1), which happens on cloud campuses only;
 *   service   auto-closed or never finished, and on a non-cloud campus also finished
 *             with no reason (it records an auto-close as reason 0 too, so the two
 *             can't be told apart) -- hence `isCloud`.
 * The scheduled PDF prints the same wording (coverageSub / coverageFootnote in
 * serverless/report_generator.py). Change both together.
 *
 * Renders nothing without coverage (a payload from before 2026-10-10, e.g. restored
 * from the report cache) or when nothing was listed.
 *
 * The Main Dashboard reuses it (user, 2026-10-22) with its own `describe`, because its
 * alert set and exclusions differ: see StatCards.
 *
 * @param {object} coverage
 * @param {string} metric       reads coverage[`${metric}_measured`]
 * @param {boolean} [isCloud]
 * @param {function} [describe] ({ total, measured, outages }) => { title, label, body, items };
 *                              defaults to the Alert Details wording ('response' | 'service')
 */

import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';

export const countOf = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

function exclusions(metric, { total, measured, outages }, isCloud) {
    const other = Math.max(0, total - measured - outages);
    const items = [];
    if (outages) {
        items.push(metric === 'response'
            ? `${countOf(outages, 'System Outage')}: the time a server was offline, not a caregiver response.`
            : `${countOf(outages, 'System Outage')}: never accepted or finished, so there is no service period.`);
    }
    if (other) {
        items.push(metric === 'response'
            ? `${countOf(other, 'alert')} the system auto-cleared: no caregiver responded.`
            : `${countOf(other, 'alert')} ${isCloud
                ? 'auto-closed by the system or never finished.'
                : 'auto-closed by the system, never finished, or finished with no reason recorded. '
                  + 'This system records an auto-close the same way as a finish without a reason, '
                  + 'so neither can be measured.'}`);
    }
    return items;
}

function reportTip(metric, counts, isCloud) {
    const name = metric === 'response' ? 'Avg Response' : 'Avg Service';
    const what = metric === 'response'
        ? 'a measurable response (alert to device cleared)'
        : 'a measurable service period (device cleared to finished)';
    return {
        title: name,
        label: `How ${name} is calculated`,
        body: `The average of the ${countOf(counts.measured, 'alert')} with ${what}.`,
        items: exclusions(metric, counts, isCloud),
    };
}

const CoverageNote = ({ coverage, metric, isCloud = false, describe }) => {
    const total = Number(coverage?.total) || 0;
    if (!total) return null;
    const measured = Number(coverage?.[`${metric}_measured`]) || 0;
    const outages = Number(coverage?.outages) || 0;
    const counts = { total, measured, outages };
    const { title, label, body, items } = describe ? describe(counts) : reportTip(metric, counts, isCloud);

    return (
        <div className="ar-summary-card__sub">
            {measured === total ? `Based on all ${countOf(total, 'alert')}` : `Based on ${measured.toLocaleString()} of ${countOf(total, 'alert')}`}
            <HeaderInfoTip
                title={title}
                label={label}
                body={body}
                /* InfoPopover renders bodies inside a <span>, so block spans, not <ul>. */
                sections={items.length ? [{
                    heading: 'Not included',
                    body: items.map((text) => (
                        <span key={text} className="ar-coverage-tip__item">{text}</span>
                    )),
                }] : undefined}
            />
        </div>
    );
};

export default CoverageNote;
